export const STANDARD_PRICE_TIER = 'standard'

const MAX_TRIAL_DAYS = 30

function positiveNumber (value) {
  const number = Number(value)
  return number > 0 ? number : null
}

function trialDaysOrNull (value) {
  if (value === null || value === undefined || value === '') return null
  const days = Number(value)
  return Number.isInteger(days) && days >= 0 && days <= MAX_TRIAL_DAYS ? days : null
}

/**
 * Turns the /api/billing/pricing payload into the shape the UI uses. Returns
 * null when the payload cannot be trusted, so callers show the unavailable
 * state instead of a guessed price or trial. `trial_days` must be a whole number
 * from 0 to 30; 0 is a real answer meaning "no trial", a missing or invalid
 * value invalidates the payload.
 */
export function normaliseWebPricingResponse (data) {
  const monthly = positiveNumber(data?.monthly)
  const annual = positiveNumber(data?.annual)
  const trialDays = trialDaysOrNull(data?.trial_days)
  if (typeof data?.currency !== 'string' || monthly === null || annual === null || trialDays === null) {
    return null
  }

  const equivalent = positiveNumber(data?.annual_monthly_equivalent)
  const offer = data?.founding_offer
  const foundingMonthly = positiveNumber(offer?.prices?.monthly)
  const foundingAnnual = positiveNumber(offer?.prices?.annual)
  const foundingEquivalent = positiveNumber(offer?.prices?.annual_monthly_equivalent)
  const foundingCurrency = typeof offer?.prices?.currency === 'string'
    ? offer.prices.currency.toUpperCase()
    : ''
  const currency = data.currency.toUpperCase()
  const foundingTier = typeof offer?.price_tier === 'string' ? offer.price_tier : ''
  const foundingMonthlyPlan = typeof offer?.plans?.monthly === 'string' ? offer.plans.monthly : ''
  const foundingAnnualPlan = typeof offer?.plans?.annual === 'string' ? offer.plans.annual : ''
  const foundingActive = offer?.active === true
    && foundingCurrency === currency
    && foundingMonthly !== null
    && foundingAnnual !== null
    && foundingTier !== ''
    && foundingMonthlyPlan !== ''
    && foundingAnnualPlan !== ''
  const seatsLeft = offer?.seats_left === null || offer?.seats_left === undefined ? NaN : Number(offer.seats_left)
  const seatCap = offer?.seat_cap === null || offer?.seat_cap === undefined ? NaN : Number(offer.seat_cap)
  const seatsKnown = foundingActive
    && Number.isFinite(seatsLeft) && seatsLeft >= 0
    && Number.isFinite(seatCap) && seatCap > 0 && seatsLeft <= seatCap

  return {
    currency,
    monthly,
    annual,
    annual_monthly_equivalent: equivalent ?? Math.floor(annual / 12),
    trial_days: trialDays,
    allow_promotion_codes: data?.allow_promotion_codes === true,
    founding_offer: {
      active: foundingActive,
      seats_left: seatsKnown ? Math.floor(seatsLeft) : null,
      seat_cap: seatsKnown ? Math.floor(seatCap) : null,
      price_tier: foundingTier,
      prices: foundingActive
        ? {
            currency: foundingCurrency,
            monthly: foundingMonthly,
            annual: foundingAnnual,
            annual_monthly_equivalent: foundingEquivalent ?? Math.floor(foundingAnnual / 12),
          }
        : null,
      plans: {
        monthly: foundingMonthlyPlan,
        annual: foundingAnnualPlan,
      },
    },
  }
}

export function webPriceTier (pricing) {
  return pricing?.founding_offer?.active === true
    ? pricing.founding_offer.price_tier
    : STANDARD_PRICE_TIER
}

export function webCheckoutPlan (pricing, selectedPlan) {
  if (pricing?.founding_offer?.active === true) {
    return pricing.founding_offer.plans[selectedPlan]
  }
  return selectedPlan
}

/**
 * What the founding price saves against the standard price the backend reports
 * for the same currency. Both sides come from Stripe via the backend, so the
 * struck-through reference is always the live standard price: when the owner
 * swaps a standard Price in the admin, this follows. Returns null when there is
 * no founding offer or nothing is saved.
 *
 * @returns {{ monthlyPercent: number, annualAmount: number, annualPercent: number } | null}
 */
export function foundingSavings (pricing) {
  const founding = pricing?.founding_offer
  if (founding?.active !== true || !founding.prices) return null
  if (founding.prices.currency !== pricing.currency) return null

  const monthlyDiff = pricing.monthly - founding.prices.monthly
  const annualDiff = pricing.annual - founding.prices.annual
  if (!(monthlyDiff > 0) || !(annualDiff > 0)) return null

  return {
    monthlyPercent: Math.round((monthlyDiff / pricing.monthly) * 100),
    annualAmount: annualDiff,
    annualPercent: Math.round((annualDiff / pricing.annual) * 100),
  }
}

/**
 * Trial wording state. `null` trial days means "not known yet or failed", which
 * must never render a number or a trial claim.
 * @returns {'trial' | 'none' | 'unknown'}
 */
export function trialState (trialDays) {
  if (trialDays === null || trialDays === undefined) return 'unknown'
  return trialDays > 0 ? 'trial' : 'none'
}

/**
 * What the founding card says about places: nothing, an always-true static
 * line, or the live count. The count only shows once a fifth of the places
 * are taken, so a fresh offer never reads "100 of 100 left". Integer maths
 * keeps 80 of 100 exactly on the boundary.
 */
export function foundingSeatsDisplay (seatsLeft, seatCap) {
  if (seatsLeft === null || seatsLeft === undefined || seatCap === null || seatCap === undefined) return 'none'
  const left = Number(seatsLeft)
  const cap = Number(seatCap)
  if (!Number.isFinite(left) || !Number.isFinite(cap) || cap <= 0 || left <= 0 || left > cap) return 'none'
  return left * 10 <= cap * 8 ? 'count' : 'static'
}
